import React, {useEffect, useState} from 'react';
import ClientDropdownList from "@clients/referral/ClientDropdownList";
import {useStateConfig} from "@/hooks/useStateConfig";
import {useRosterStore} from "@/store/roster_store";
import {useFindHelp} from "@/hooks/useFindHelp";
import {createFormFieldConfig, useForm} from "@/hooks/useForm";
import Validate from "@/helpers/validate";
import {BannerTypes} from "patient-ping-remedy/packages/banner";
import Button, {StyleType} from "patient-ping-remedy/packages/button";
import {css} from "@emotion/css";
import RadioButton from "patient-ping-remedy/packages/radio-button";
import Modal from "patient-ping-remedy/packages/modal";
import {sizes} from "patient-ping-remedy/packages/theme";
import {SSOLaunchMethod} from "@/api/dto/mixpanel";

enum ReferralOption {
  GO_DIRECTLY = "goDirectly",
  CREATE_REFERRAL = "createReferral"
}

type Props = {
  hideModal(): void;
}

export default function NavigationModal(props: Props) {
  const stateConfig = useStateConfig();
  const { currentRoster } = useRosterStore();
  const { openFindHelp } = useFindHelp();
  const [selectedOption, setSelectedOption] = useState<ReferralOption>(ReferralOption.GO_DIRECTLY);

  const { form, setInitialForm, handleChange, handleSubmit } = useForm(async () => {
    props.hideModal();
    await openFindHelp(currentRoster!.id, form.client?.value?.value,
      selectedOption === ReferralOption.GO_DIRECTLY, SSOLaunchMethod.APPLICATION_MENU);
  });

  function handleHideModal(e: React.MouseEvent<HTMLButtonElement | HTMLDivElement>){
    if (e.target instanceof HTMLDivElement || e.target instanceof HTMLSpanElement) {
      return;
    }
    props.hideModal();
  }

  useEffect(() => {
    setInitialForm({
      ...createFormFieldConfig(
        'client',
        null,
        { valid: false, message: '' },
        [Validate.required],
        true,
      ),
    });
  }, [setInitialForm]);

  const modalButtons = [
    <Button
      key="go"
      styleType={StyleType.PRIMARY}
      disabled={(selectedOption === ReferralOption.CREATE_REFERRAL && !form.client?.value)}
      onClick={handleSubmit}
    >
      Go
    </Button>,
    <Button key="cancel" styleType={StyleType.TERTIARY} onClick={props.hideModal}>
      Cancel
    </Button>
  ];

  return (
    <Modal
      id="FindHelp-modal"
      headerText={stateConfig?.branding.findHelp.service}
      banner={
        selectedOption === ReferralOption.GO_DIRECTLY ? {
          type: BannerTypes.PATIENT_SAFETY,
          text: `You must create a referral to link a client from ${stateConfig?.branding.findHelp.service}.`
        } : undefined
      }
      buttons={modalButtons}
      hideModal={handleHideModal}
      className={css({ width: '70rem' })}
    >
      <RadioButton
        name="chooseReferralOption"
        value={ReferralOption.GO_DIRECTLY}
        id="goDirectly"
        label={`Go directly to ${stateConfig?.branding.findHelp.service}`}
        checked={selectedOption === ReferralOption.GO_DIRECTLY}
        onChange={(e) => setSelectedOption(e.target.value as ReferralOption)}
      />
      <RadioButton
        name="chooseReferralOption"
        value={ReferralOption.CREATE_REFERRAL}
        id="createReferral"
        label="Create a referral"
        checked={selectedOption === ReferralOption.CREATE_REFERRAL}
        onChange={(e) => setSelectedOption(e.target.value as ReferralOption)}
      />
      {selectedOption === ReferralOption.CREATE_REFERRAL &&
        <ClientDropdownList
          id="referral-client"
          form={form}
          handleChange={handleChange}
          required
          isWithinModal
          className={css({ margin: sizes.medium })}
        />
      }
    </Modal>
  );
}
